/**
 * Cost-ranked routing — shadow-dispatch-comparison visibility panel
 * (routing_decision_visibility_gap_2026_09_01 todo 3 — the last of four: the
 * reporter (todo 0), persistence (todo 1), and read API (todo 2) all shipped
 * 2026-09-01; this is the first UI surface for any of it). Previously the only way
 * to see `UsagePoller`'s cost-ranked shadow-comparison tick was a `logger.info` line
 * every ~30 min. Shows:
 *
 *   - % of ticks where the cost-ranked pick would have differed from the actual
 *     dispatch, and the aggregate $ delta (shadow − actual) over the fetched
 *     persisted history.
 *   - which model the pipeline is CURRENTLY favoring per role_group, replayed live
 *     against today's real completions (never persisted — see the live route's own
 *     docstring in server/routes/backlog.py).
 *
 * SHADOW MODE ONLY — none of this affects real dispatch.
 * wire_routing_cost_into_live_dispatch_2026_08_31 is the separate, still-open
 * follow-up that would make any of this the real routing decision.
 */

import { useEffect, useState } from "react";

import { api } from "./api";
import { fmtPctOrDash } from "./BatchingEfficiencyPanel";
import { Panel } from "./components";
import { ROLE_GROUP_FILTER_OPTIONS, fmtUsdOrDash } from "./TaskUsageWindows";
import type { AccountView, RoutingShadowComparisonRowView, RoutingShadowComparisonSummaryView } from "./types";

const POLL_INTERVAL_MS = 60000;
const HISTORY_LIMIT = 100;
const HISTORY_ROWS_SHOWN = 20;
const LIVE_WINDOW_MINUTES = 30;

/** role_group -> its dashboard label, per TaskUsageWindows' own filter options —
 * falls back to the raw value for a role_group with no registered label (never
 * blanks it). */
function roleGroupLabel(role: string): string {
  return ROLE_GROUP_FILTER_OPTIONS.find((o) => o.roleGroup === role)?.label ?? role;
}

/** "+$0.0123" / "-$0.0123" — sign-explicit (a bare "$0.0123" would read as "this is
 * what shadow costs in total", not "this much MORE/LESS than actual"). An exact zero
 * delta renders unsigned ("$0.0000"), never a dash — that IS a real measurement (a
 * genuine tie), not missing data. */
export function fmtUsdDelta(deltaUsd: number): string {
  if (deltaUsd === 0) return "$0.0000";
  const sign = deltaUsd > 0 ? "+" : "-";
  return `${sign}${fmtUsdOrDash(Math.abs(deltaUsd))}`;
}

/** One tick's differed-% — null when the tick resolved nothing (never a fabricated
 * 0/0 -> 0%). */
export function tickDifferedPct(tick: RoutingShadowComparisonSummaryView): number | null {
  if (tick.resolved <= 0) return null;
  return (tick.would_have_differed / tick.resolved) * 100;
}

/** One tick's $ delta — shadow minus actual; positive means the cost-ranked pick
 * would have cost MORE, negative means it would have SAVED money. */
export function tickDollarDelta(tick: RoutingShadowComparisonSummaryView): number {
  return tick.shadow_total_usd - tick.actual_total_usd;
}

/** Aggregate differed-% across every fetched tick — sums numerator/denominator
 * across ticks FIRST, then divides once, rather than averaging each tick's own %: a
 * tick with 1 resolved task must not carry the same weight as one with 50 (per-tick
 * averaging would let a single near-empty tick swing the headline number). Null when
 * nothing in the fetched history resolved anything. */
export function aggregateDifferedPct(history: RoutingShadowComparisonSummaryView[]): number | null {
  const resolved = history.reduce((sum, t) => sum + t.resolved, 0);
  if (resolved <= 0) return null;
  const differed = history.reduce((sum, t) => sum + t.would_have_differed, 0);
  return (differed / resolved) * 100;
}

/** Aggregate $ delta (shadow − actual) summed across every fetched tick. */
export function aggregateDollarDelta(history: RoutingShadowComparisonSummaryView[]): number {
  return history.reduce((sum, t) => sum + (t.shadow_total_usd - t.actual_total_usd), 0);
}

export interface FavoredPick {
  role: string;
  model: string;
  provider: string | null;
  pickCount: number;
  roleTotal: number;
  differedCount: number;
}

/** Which model the pipeline is CURRENTLY favoring per role_group — the mode of
 * `shadow_model` among this role's live-replayed rows (ties broken by first-seen
 * model — a plain JS `Map` preserves insertion order, and `rows` arrives newest-first
 * from the backend, so this is deterministic run to run). Rows with no shadow pick
 * (`shadow_model === null`, e.g. an empty account pool) are excluded from the vote
 * but still counted in `roleTotal` — a role_group dominated by unresolvable rows
 * should read as sparse, not silently drop those rows from the denominator. A role
 * with EVERY row unresolved has no winner and is dropped entirely (nothing to favor).
 * `provider` is looked up from `accounts` by the WINNING pick's own
 * `shadow_account_id` (not `actual`'s account) — falls back to null (never a guessed
 * provider) when the account isn't in the registry (e.g. since retired). */
export function favoredPicksByRoleGroup(
  rows: RoutingShadowComparisonRowView[],
  accounts: AccountView[],
): FavoredPick[] {
  const byRole = new Map<string, RoutingShadowComparisonRowView[]>();
  for (const r of rows) {
    const list = byRole.get(r.role);
    if (list) list.push(r);
    else byRole.set(r.role, [r]);
  }
  const out: FavoredPick[] = [];
  for (const [role, roleRows] of byRole) {
    const votes = new Map<string, { count: number; accountId: string | null }>();
    for (const r of roleRows) {
      if (r.shadow_model === null) continue;
      const existing = votes.get(r.shadow_model);
      if (existing) existing.count += 1;
      else votes.set(r.shadow_model, { count: 1, accountId: r.shadow_account_id });
    }
    let winner: { model: string; count: number; accountId: string | null } | null = null;
    for (const [model, v] of votes) {
      if (winner === null || v.count > winner.count) winner = { model, count: v.count, accountId: v.accountId };
    }
    if (winner === null) continue;
    const provider = accounts.find((a) => a.account_id === winner.accountId)?.provider ?? null;
    const differedCount = roleRows.filter((r) => r.would_have_differed).length;
    out.push({
      role,
      model: winner.model,
      provider,
      pickCount: winner.count,
      roleTotal: roleRows.length,
      differedCount,
    });
  }
  return out.sort((a, b) => roleGroupLabel(a.role).localeCompare(roleGroupLabel(b.role)));
}

export function RoutingShadowComparisonPanel({ baseUrl }: { baseUrl: string }) {
  const [history, setHistory] = useState<RoutingShadowComparisonSummaryView[]>([]);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const [liveRows, setLiveRows] = useState<RoutingShadowComparisonRowView[]>([]);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [liveLoaded, setLiveLoaded] = useState(false);

  const [accounts, setAccounts] = useState<AccountView[]>([]);

  // Registry fetch, once — same "degrade to empty, never blank the panel" contract
  // as every other panel's account-registry effect (TaskUsageWindows/
  // BatchingEfficiency).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await api.listAccounts(baseUrl);
        if (!cancelled) setAccounts(data);
      } catch {
        if (!cancelled) setAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getRoutingShadowComparisonHistory(baseUrl, HISTORY_LIMIT);
        if (!cancelled) {
          setHistory(data);
          setHistoryError(null);
          setHistoryLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setHistoryError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.getRoutingShadowComparisonLive(baseUrl, LIVE_WINDOW_MINUTES);
        if (!cancelled) {
          setLiveRows(data);
          setLiveError(null);
          setLiveLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setLiveError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    const id = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [baseUrl]);

  const differedPct = aggregateDifferedPct(history);
  const dollarDelta = aggregateDollarDelta(history);
  const favored = favoredPicksByRoleGroup(liveRows, accounts);
  const recentHistory = history.slice(0, HISTORY_ROWS_SHOWN);

  return (
    <Panel title="Cost-Ranked Routing (Shadow)" tone="accounts">
      <div className="usage-panel-hint">
        Shadow mode only — replays real completions through the cost-ranked pipeline for comparison; never affects real
        dispatch. Live cutover is a separate, still-open follow-up (wire_routing_cost_into_live_dispatch_2026_08_31).
      </div>
      {historyError && <div className="task-usage-error">Failed to load history: {historyError}</div>}
      {!historyError && !historyLoaded && <div className="task-usage-pending">loading…</div>}
      {!historyError && historyLoaded && history.length === 0 && (
        <div className="task-usage-pending">no shadow-comparison ticks recorded yet</div>
      )}
      {!historyError && history.length > 0 && (
        <>
          <div className="usage-panel-hint" data-testid="routing-shadow-comparison-summary">
            Across the last {history.length} tick{history.length === 1 ? "" : "s"}: cost-ranked pick would have differed{" "}
            <b>{fmtPctOrDash(differedPct)}</b> of the time · net $ delta (shadow − actual):{" "}
            <b>{fmtUsdDelta(dollarDelta)}</b>
          </div>
          <table className="task-usage-window-table" data-testid="routing-shadow-comparison-history-table">
            <thead>
              <tr>
                <th>Tick</th>
                <th>Sampled</th>
                <th>Resolved</th>
                <th>Differed</th>
                <th>Differed %</th>
                <th>Actual $</th>
                <th>Shadow $</th>
                <th>Δ $</th>
              </tr>
            </thead>
            <tbody>
              {recentHistory.map((t) => (
                <tr key={t.id}>
                  <td className="task-usage-window-label">{new Date(t.ts).toLocaleString()}</td>
                  <td className="mono">{t.sampled}</td>
                  <td className="mono">{t.resolved}</td>
                  <td className="mono">{t.would_have_differed}</td>
                  <td className="mono">{fmtPctOrDash(tickDifferedPct(t))}</td>
                  <td className="mono">{fmtUsdOrDash(t.actual_total_usd)}</td>
                  <td className="mono">{fmtUsdOrDash(t.shadow_total_usd)}</td>
                  <td className="mono">{fmtUsdDelta(tickDollarDelta(t))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <div className="usage-panel-hint" style={{ marginTop: 10 }}>
        Currently favored pick per role_group — replayed live over the trailing {LIVE_WINDOW_MINUTES} min, never
        persisted.
      </div>
      {liveError && <div className="task-usage-error">Failed to load live detail: {liveError}</div>}
      {!liveError && !liveLoaded && <div className="task-usage-pending">loading…</div>}
      {!liveError && liveLoaded && favored.length === 0 && (
        <div className="task-usage-pending">no completions in the trailing {LIVE_WINDOW_MINUTES} min to replay</div>
      )}
      {!liveError && favored.length > 0 && (
        <table className="task-usage-window-table" data-testid="routing-shadow-comparison-favored-table">
          <thead>
            <tr>
              <th>Role group</th>
              <th>Favored model</th>
              <th>Provider</th>
              <th>Picks</th>
              <th>Would differ</th>
            </tr>
          </thead>
          <tbody>
            {favored.map((f) => (
              <tr key={f.role}>
                <td className="task-usage-window-label">{roleGroupLabel(f.role)}</td>
                <td className="mono">{f.model}</td>
                <td className="mono">{f.provider ?? "—"}</td>
                <td className="mono">
                  {f.pickCount}/{f.roleTotal}
                </td>
                <td className="mono">
                  {f.differedCount}/{f.roleTotal} (
                  {fmtPctOrDash(f.roleTotal > 0 ? (f.differedCount / f.roleTotal) * 100 : null)})
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}
